import React, { useState, useEffect, useContext, useRef, useMemo } from "react"
import { Modal, Dropdown, Menu, Empty, Spin, message, Tooltip } from "antd"
import { Send, User, Users, Check, Search as SearchIcon, X, Globe } from "react-feather"
import { useQuery, useMutation } from "@tanstack/react-query"
import { AuthContext } from "../../contexts/auth/AuthContext"
import { ThemeContext } from "../../contexts/ThemeContex"
import {
  GET_CHAT_MESSAGES,
  SEND_CHAT_MESSAGE,
  MARK_CHAT_AS_READ,
  GET_CHAT_CHANNELS,
} from "../../rest-api/queries/chat"
import { playNotificationSound } from "../../utils/notificationSounds"
import { useTranslation } from "react-i18next"
import {
  translateText,
  currentLang,
  LANG_LABELS,
  SupportedLang,
} from "../../utils/aiTranslate"
import "./ChatModal.less"

/** Ro'yxatdagi vaqt: bugungi bo'lsa soat, aks holda sana */
function formatTime(value?: string | null) {
  if (!value) return ""
  const d = new Date(value)
  if (isNaN(d.getTime())) return ""
  const today = new Date()
  const sameDay =
    d.getDate() === today.getDate() &&
    d.getMonth() === today.getMonth() &&
    d.getFullYear() === today.getFullYear()
  return sameDay
    ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString([], { day: "2-digit", month: "2-digit" })
}

function clockOf(value?: string | null) {
  if (!value) return ""
  const d = new Date(value)
  if (isNaN(d.getTime())) return ""
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
}

/** Xabarlar orasidagi kun ajratgichi uchun kalit */
function dayKey(value?: string | null) {
  if (!value) return ""
  const d = new Date(value)
  if (isNaN(d.getTime())) return ""
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
}

/**
 * Avatar rangi ism bo'yicha barqaror tanlanadi — Telegramdagidek
 * bir odam har doim bir xil rangda ko'rinadi.
 */
const AVATAR_COLORS = [
  "#e17076",
  "#7bc862",
  "#e5ca77",
  "#65aadd",
  "#a695e7",
  "#ee7aae",
  "#6ec9cb",
]

function colorOf(name?: string | null) {
  const s = String(name || "?")
  let sum = 0
  for (let i = 0; i < s.length; i++) sum += s.charCodeAt(i)
  return AVATAR_COLORS[sum % AVATAR_COLORS.length]
}

function initialsOf(name?: string | null) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
  if (parts.length === 0) return "?"
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase()
  return (parts[0][0] + parts[1][0]).toUpperCase()
}

function Avatar({
  name,
  icon,
  color,
}: {
  name?: string | null
  icon?: React.ReactNode
  color?: string
}) {
  return (
    <div className="tg__avatar" style={{ background: color || colorOf(name) }}>
      {icon || initialsOf(name)}
    </div>
  )
}

export default function ChatModal({
  visible,
  onClose,
}: {
  visible: boolean
  onClose: () => void
}) {
  const { authState } = useContext(AuthContext)
  const { themeState } = useContext(ThemeContext)
  const isDark = themeState.currentTheme === "dark"
  const { t } = useTranslation()

  const currentUserName =
    authState.user?.manager?.name ||
    authState.user?.userName ||
    String(t("managerWord"))
  const isAdminUser = authState.user?.userName === "Administrator"

  // Xabarlarni AI orqali o'z tiliga o'girish (guruh talabi)
  const [translations, setTranslations] = useState<Record<number, string>>({})
  const [translatingId, setTranslatingId] = useState<number | null>(null)

  const toggleTranslate = async (msg: any) => {
    const id = msg.id
    if (translations[id]) {
      // Ikkinchi bosishda asl matn qaytadi
      setTranslations((prev) => {
        const next = { ...prev }
        delete next[id]
        return next
      })
      return
    }
    setTranslatingId(id)
    try {
      const result = await translateText(msg.text || "", currentLang())
      setTranslations((prev) => ({ ...prev, [id]: result }))
    } finally {
      setTranslatingId(null)
    }
  }

  const [selectedChannel, setSelectedChannel] = useState<string>("group")
  const [inputVal, setInputVal] = useState("")
  const [search, setSearch] = useState("")

  // Yuborishdan oldin matnni tarjima qilish
  const [composeTranslating, setComposeTranslating] = useState(false)

  const translateCompose = async (lang: SupportedLang) => {
    if (!inputVal.trim()) return
    setComposeTranslating(true)
    try {
      setInputVal(await translateText(inputVal, lang))
    } finally {
      setComposeTranslating(false)
    }
  }

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const prevMsgCountRef = useRef<number>(0)

  // Xonalar ro'yxati backenddan ruxsatlar bo'yicha keladi (RBAC talabnomasi):
  // oddiy xodim faqat "Umumiy guruh" va "Admin bilan aloqa" ni ko'radi,
  // Glavniy Admin esa har bir xodim bilan alohida xonani ko'radi.
  const {
    data: channels = [],
    isLoading: loadingManagers,
    refetch: refetchChannels,
  } = useQuery(["GET_CHAT_CHANNELS"], GET_CHAT_CHANNELS, {
    enabled: visible,
    // O'qilmaganlar hisoblagichi tirik bo'lishi uchun ro'yxat ham yangilanib turadi
    refetchInterval: visible ? 5000 : false,
  })
  const directChannels = channels.filter((c) => c.id !== "group")
  const groupChannel = channels.find((c) => c.id === "group")
  // Backend admin ga har bir xodim uchun alohida xona qaytaradi, oddiy xodimga esa
  // faqat bittasini — shuning uchun ro'yxat uzunligi ham admin belgisini beradi
  const isSuperAdmin = isAdminUser || directChannels.length > 1

  const visibleDirect = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return directChannels
    return directChannels.filter((c) =>
      String(c.name || "").toLowerCase().includes(q)
    )
  }, [directChannels, search])

  const showGroup =
    !search.trim() ||
    String(t("generalGroup")).toLowerCase().includes(search.trim().toLowerCase())

  // Fetch messages from backend
  const {
    data: chatData,
    isLoading: loadingChat,
    refetch: refetchChat,
  } = useQuery(
    ["GET_CHAT_MESSAGES", selectedChannel],
    () =>
      GET_CHAT_MESSAGES({ channelId: selectedChannel, page: 1, pageSize: 100 }),
    {
      enabled: visible,
      refetchInterval: visible ? 3000 : false, // Poll every 3s while open
    }
  )

  const messages: any[] = chatData?.data || chatData?.Data || []

  // Mutation to send message
  const { mutate: sendMessageMutation, isLoading: sending } = useMutation(
    SEND_CHAT_MESSAGE,
    {
      onSuccess: () => {
        setInputVal("")
        refetchChat()
        playNotificationSound()
      },
      // Xato bo'lsa yozilgan matn yo'qolmaydi va sabab ko'rinadi — ilgari
      // xabar yuborilmaganda hech qanday belgi bo'lmasdi
      onError: (err: any) => {
        const detail =
          err?.response?.data?.Message ||
          err?.response?.data?.message ||
          err?.message ||
          t("unknownError")
        message.error(`${t("messageNotSent")}: ${detail}`)
      },
    }
  )

  // Yangi xabar kelganda ovoz va ekran burchagida bildirishnoma (talabnoma, 5.3)
  useEffect(() => {
    if (messages.length > prevMsgCountRef.current && prevMsgCountRef.current > 0) {
      const lastMsg = messages[messages.length - 1]
      const sender = lastMsg?.senderName || lastMsg?.SenderName
      if (sender && sender !== currentUserName) {
        playNotificationSound()
        const text = lastMsg?.text || lastMsg?.Text || ""
        message.info(
          `${sender}: ${text.length > 60 ? text.slice(0, 60) + "..." : text}`
        )
      }
    }
    prevMsgCountRef.current = messages.length
  }, [messages, currentUserName])

  useEffect(() => {
    if (visible) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
      // O'qilgan deb belgilangach hisoblagich ham yangilanadi
      MARK_CHAT_AS_READ(selectedChannel)
        .then(() => refetchChannels())
        .catch(() => {})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, selectedChannel, messages.length])

  const handleSend = () => {
    if (!inputVal.trim() || sending) return
    sendMessageMutation({
      channelId: selectedChannel,
      senderId:
        authState.user?.id?.toString() || (isSuperAdmin ? "admin" : "user"),
      senderName: currentUserName,
      text: inputVal.trim(),
      isAdmin: isSuperAdmin,
    })
  }

  const selectedManager =
    selectedChannel === "group"
      ? null
      : directChannels.find((c) => c.id === selectedChannel)

  const isGroup = selectedChannel === "group"
  const headName = isGroup
    ? t("generalGroupChat")
    : selectedManager?.name || t("managerWord")
  const headSub = isGroup
    ? t("adminAndAllManagers")
    : isSuperAdmin
    ? t("privateConversation")
    : t("onlyYouAndAdmin")

  /** Kun ajratgichi yorlig'i: bugun / kecha / to'liq sana */
  const dayLabel = (value?: string | null) => {
    const d = new Date(value || "")
    if (isNaN(d.getTime())) return ""
    const now = new Date()
    const diff = Math.round(
      (new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime() -
        new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()) /
        86400000
    )
    if (diff === 0) return String(t("today"))
    if (diff === 1) return String(t("yesterday"))
    return d.toLocaleDateString([], {
      day: "2-digit",
      month: "long",
      year: d.getFullYear() === now.getFullYear() ? undefined : "numeric",
    })
  }

  const isMine = (msg: any) =>
    msg.senderName === currentUserName ||
    msg.senderName === authState.user?.userName ||
    (msg.senderId && String(msg.senderId) === String(authState.user?.id))

  const langMenu = (
    <Menu
      className="tg__lang-menu"
      onClick={({ key }) => translateCompose(key as SupportedLang)}
      items={(["uz", "ru", "en"] as SupportedLang[]).map((l) => ({
        key: l,
        label: LANG_LABELS[l],
      }))}
    />
  )

  const renderChatRow = (
    id: string,
    name: string,
    preview: string,
    time: string,
    unread: number,
    icon?: React.ReactNode,
    color?: string
  ) => (
    <div
      key={id}
      className={`tg__chat${selectedChannel === id ? " is-active" : ""}`}
      onClick={() => setSelectedChannel(id)}
    >
      <Avatar name={name} icon={icon} color={color} />
      <div className="tg__chat-body">
        <div className="tg__chat-row">
          <div className="tg__chat-name">{name}</div>
          {time && <div className="tg__chat-time">{time}</div>}
        </div>
        <div className="tg__chat-preview">
          <span>{preview}</span>
          {unread > 0 && (
            <div className="tg__badge">{unread > 99 ? "99+" : unread}</div>
          )}
        </div>
      </div>
    </div>
  )

  return (
    <Modal
      className="tg-modal"
      title={null}
      closable={false}
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={940}
      bodyStyle={{ padding: 0 }}
      centered
      destroyOnClose
    >
      <div className={`tg${isDark ? " tg--dark" : ""}`}>
        {/* Chapda: chatlar ro'yxati */}
        <aside className="tg__sidebar">
          <div className="tg__sidebar-head">
            <div className="tg__brand">{t("chatTitle")}</div>
            <div className="tg__search">
              <SearchIcon size={16} />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={String(t("search"))}
              />
              {search && (
                <X
                  size={15}
                  style={{ cursor: "pointer" }}
                  onClick={() => setSearch("")}
                />
              )}
            </div>
          </div>

          <div className="tg__chats">
            {showGroup &&
              renderChatRow(
                "group",
                String(t("generalGroup")),
                groupChannel?.lastMessageText
                  ? `${groupChannel.lastMessageSender || ""}: ${
                      groupChannel.lastMessageText
                    }`
                  : String(t("allStaffChat")),
                formatTime(groupChannel?.lastMessageAt),
                groupChannel?.unreadCount || 0,
                <Users size={20} />,
                "#3390ec"
              )}

            {loadingManagers ? (
              <div style={{ textAlign: "center", padding: 20 }}>
                <Spin size="small" />
              </div>
            ) : (
              visibleDirect.map((chan) =>
                renderChatRow(
                  chan.id,
                  chan.name || String(t("contactWithAdmin")),
                  chan.lastMessageText
                    ? `${chan.lastMessageSender || ""}: ${chan.lastMessageText}`
                    : isSuperAdmin
                    ? String(t("privateConversation"))
                    : String(t("onlyYouAndAdmin")),
                  formatTime(chan.lastMessageAt),
                  chan.unreadCount || 0,
                  chan.name ? undefined : <User size={18} />
                )
              )
            )}
          </div>
        </aside>

        {/* O'ngda: suhbat */}
        <section className="tg__main">
          <header className="tg__head">
            <Avatar
              name={isGroup ? undefined : selectedManager?.name}
              icon={isGroup ? <Users size={18} /> : undefined}
              color={isGroup ? "#3390ec" : undefined}
            />
            <div className="tg__head-body">
              <div className="tg__head-name">{headName}</div>
              {/* Backend a'zolar sonini yoki onlayn holatni bermaydi,
                  shuning uchun bu yerda faqat aniq ma'lum narsa yoziladi */}
              <div className="tg__head-sub">{headSub}</div>
            </div>
            <button className="tg__icon-btn" onClick={onClose} title={String(t("close"))}>
              <X size={20} />
            </button>
          </header>

          <div className="tg__feed">
            {loadingChat && messages.length === 0 ? (
              <div className="tg__feed-center">
                <Spin />
              </div>
            ) : messages.length === 0 ? (
              <div className="tg__feed-center">
                <Empty description={t("noMessagesYet")} />
              </div>
            ) : (
              messages.map((msg: any, i: number) => {
                const mine = isMine(msg)
                const prev = messages[i - 1]
                const next = messages[i + 1]
                // Ketma-ket kelgan xabarlar bitta guruh: ism faqat birinchisida,
                // dumcha faqat oxirgisida — Telegramdagidek
                const newDay = dayKey(msg.createdAt) !== dayKey(prev?.createdAt)
                const firstOfGroup =
                  newDay || !prev || isMine(prev) !== mine ||
                  prev.senderName !== msg.senderName
                const lastOfGroup =
                  !next ||
                  isMine(next) !== mine ||
                  next.senderName !== msg.senderName ||
                  dayKey(next.createdAt) !== dayKey(msg.createdAt)

                return (
                  <React.Fragment key={msg.id || i}>
                    {newDay && (
                      <div className="tg__day">{dayLabel(msg.createdAt)}</div>
                    )}
                    <div
                      className={`tg__msg${mine ? " is-mine" : ""}${
                        lastOfGroup ? " is-last" : ""
                      }`}
                    >
                      {!mine && firstOfGroup && (
                        <div
                          className={`tg__msg-sender${
                            msg.isAdmin ? " is-admin" : ""
                          }`}
                        >
                          {msg.senderName}
                          {msg.isAdmin && " (Admin)"}
                        </div>
                      )}

                      <div className="tg__bubble">
                        {translations[msg.id] || msg.text}
                        {translations[msg.id] && (
                          <div className="tg__translated">
                            {t("translate")} · {LANG_LABELS[currentLang()]}
                          </div>
                        )}
                        <div className="tg__meta">
                          <span>{clockOf(msg.createdAt)}</span>
                          {mine && (
                            <Tooltip
                              title={
                                msg.isRead || msg.IsRead
                                  ? t("msgRead")
                                  : t("msgDelivered")
                              }
                            >
                              <span
                                className={`tg__ticks${
                                  msg.isRead || msg.IsRead ? " is-read" : ""
                                }`}
                              >
                                <Check size={13} />
                                {(msg.isRead || msg.IsRead) && <Check size={13} />}
                              </span>
                            </Tooltip>
                          )}
                        </div>
                      </div>

                      {!mine && (
                        <div className="tg__msg-tools">
                          <button
                            onClick={() => toggleTranslate(msg)}
                            disabled={translatingId === msg.id}
                          >
                            {translatingId === msg.id
                              ? "..."
                              : translations[msg.id]
                              ? t("showOriginal")
                              : t("translate")}
                          </button>
                        </div>
                      )}
                    </div>
                  </React.Fragment>
                )
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          <footer className="tg__composer">
            <div className="tg__composer-box">
              {/*
                Yuborishdan oldin matnni boshqa tilga o'girish. Ilgari bu
                pastki panelda alohida ro'yxat va tugma bo'lib joy egallardi —
                endi bitta belgi ostiga yig'ilgan.
              */}
              <Dropdown
                overlay={langMenu}
                trigger={["click"]}
                disabled={!inputVal.trim() || composeTranslating}
              >
                <button
                  className="tg__icon-btn"
                  title={String(t("translate"))}
                  disabled={!inputVal.trim() || composeTranslating}
                >
                  {composeTranslating ? <Spin size="small" /> : <Globe size={20} />}
                </button>
              </Dropdown>

              <textarea
                rows={1}
                value={inputVal}
                placeholder={String(t("writeYourMessage"))}
                onChange={(e) => {
                  setInputVal(e.target.value)
                  e.target.style.height = "auto"
                  e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px"
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault()
                    handleSend()
                  }
                }}
              />
            </div>

            <button
              className="tg__send"
              onClick={handleSend}
              disabled={!inputVal.trim() || sending}
              title={String(t("send"))}
            >
              {sending ? <Spin size="small" /> : <Send size={20} />}
            </button>
          </footer>
        </section>
      </div>
    </Modal>
  )
}
